import { Texture } from 'pixi.js';
import type { HeroId } from '@knight/shared';

/**
 * Procedural pixel art. Patterns are rows of characters mapped to palette roles:
 * '.' transparent, 'o' outline, 'b' base, 'd' dark base, 'l' light, 'a' accent,
 * 'e' eye, 'w' white, 'm' metal, 'r' red, 'k' skin, 'h' wood, 'g' grass.
 * Every solid pixel gets automatic top highlight / bottom shade for volume.
 */
type Palette = Partial<Record<string, string>>;

const cache = new Map<string, Texture>();

export function shade(hex: string, f: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, Math.min(255, Math.round(((n >> 16) & 255) * f)));
  const g = Math.max(0, Math.min(255, Math.round(((n >> 8) & 255) * f)));
  const b = Math.max(0, Math.min(255, Math.round((n & 255) * f)));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

function isSolid(c: string | undefined): boolean {
  return !!c && c !== '.' && c !== ' ' && c !== 'o';
}

export function patternTexture(key: string, rows: string[], palette: Palette, shading = true): Texture {
  const hit = cache.get(key);
  if (hit) return hit;
  const h = rows.length;
  const w = Math.max(...rows.map((r) => r.length));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  for (let y = 0; y < h; y++) {
    const row = rows[y];
    for (let x = 0; x < row.length; x++) {
      const c = row[x];
      if (c === '.' || c === ' ') continue;
      let col = palette[c];
      if (!col) continue;
      if (shading && c !== 'o' && c !== 'e' && c !== 'w') {
        const above = rows[y - 1]?.[x];
        const below = rows[y + 1]?.[x];
        if (!isSolid(above)) col = shade(col, 1.28);
        else if (!isSolid(below)) col = shade(col, 0.7);
      }
      ctx.fillStyle = col;
      ctx.fillRect(x, y, 1, 1);
    }
  }
  const tex = Texture.from(canvas);
  tex.source.scaleMode = 'nearest';
  cache.set(key, tex);
  return tex;
}

function basePalette(base: string, accent: string): Palette {
  return { o: '#120d14', b: base, d: shade(base, 0.62), l: shade(base, 1.35), a: accent, e: '#ffe9a8', w: '#f4f1ea', m: '#b9c2cc', r: '#c0304f', k: '#e8c39e', h: '#8a5a2a', g: '#3f6f3a' };
}

// ------------------------------------------------------------------ heroes (16x20)

const HERO_PATTERNS: Partial<Record<HeroId, string[]>> = {
  revenant: [
    '....oo....oo....',
    '...ommo..ommo...',
    '...ommmoommmo...',
    '....ommmmmmo....',
    '....ommeemmo....',
    '....ommmmmmo....',
    '...oommmmmmoo...',
    '..oaadbbbbdaao..',
    '.oaaddbbbbddaao.',
    'oaaddbbbbbbddaao',
    'oaaddbbbbbbddaao',
    'oaadbbbbbbbbdaao',
    '.oobbbbbbbbbboo.',
    '..obbboooobbbo..',
    '..obbo....obbo..',
    '..obbo....obbo..',
    '..oddo....oddo..',
    '..oddo....oddo..',
    '.ommmo....ommmo.',
    '.ooooo....ooooo.',
  ],
  pilgrim: [
    '......oooo......',
    '.....odddbo.....',
    '....oddbbbbo....',
    '....odbbbbbo....',
    '....odkkkkdo....',
    '....odkeekdo....',
    '.....odkkdo.h...',
    '....oabbbbao.h..',
    '...oaabbbbaaoh..',
    '..oabbbbbbbbaoh.',
    '..oabbbbbbbbaoh.',
    '..oabbbbbbbbaoh.',
    '...obbbbbbbbo.h.',
    '...obbbbbbbbo.h.',
    '...obbboobbbo.h.',
    '...obbo..obbo...',
    '...oddo..oddo...',
    '...oddo..oddo...',
    '..ommmo..ommmo..',
    '..ooooo..ooooo..',
  ],
  hexer: [
    '.......oo.......',
    '......oddo......',
    '......oddo......',
    '.....oddddo.....',
    '.....oddddo..o..',
    'oddddddddddddddo',
    '....ookeekoo...a',
    '.....okkkko...a.',
    '....oabbbbao..a.',
    '...oaabbbbaao.a.',
    '..oabbbbbbbbaoa.',
    '..oabbbbbbbbaoa.',
    '..oabbbbbbbbaoa.',
    '..oabbbbbbbbaoa.',
    '...obbbbbbbbo.a.',
    '...obbbbbbbbo.a.',
    '...obbbbbbbbo.a.',
    '..oddddddddddoa.',
    '..oooooooooooo..',
    '................',
  ],
};

export function heroTexture(hero: HeroId, base: string, accent: string): Texture {
  return patternTexture('hero2:' + hero + base + accent, HERO_PATTERNS[hero] ?? HERO_PATTERNS.revenant!, basePalette(base, accent));
}

// ------------------------------------------------------------------ monsters by shape

const SHAPES: Record<string, string[]> = {
  humanoid: [
    '.....oooo.....',
    '....obbbbo....',
    '....obeebo....',
    '....obbbbo....',
    '.....obbo.....',
    '...oabbbbao...',
    '..oabbbbbbao..',
    '..oabbbbbbao..',
    '..oabbbbbbao..',
    '...obbbbbbo...',
    '...obbo.bbo...',
    '...odo..odo...',
    '...odo..odo...',
    '..oddo..oddo..',
  ],
  beast: [
    '..............',
    '..............',
    '.oo........oo.',
    'obbo......obbo',
    'obbbooooooobbo',
    '.obbbbbbbbbbo.',
    'obebbbbbbbbbdo',
    'obbbbbbbbbbbdo',
    '.obbbbbbbbbdo.',
    '..obbbbbbbbo..',
    '..obo.oo..obo.',
    '..odo.....odo.',
    '..odo.....odo.',
    '.oddo....oddo.',
  ],
  blob: [
    '..............',
    '..............',
    '....oooooo....',
    '..oobbbbbboo..',
    '.obbbbbbbbbbo.',
    '.obbebbbbebbo.',
    'obbbbbbbbbbbbo',
    'obbbbbbbbbbbbo',
    'obbbbbbbbbbbbo',
    'obbbbbbbbbbbbo',
    '.obbbbbbbbbbo.',
    '.oddbbbbbbddo.',
    '..ooddddddoo..',
    '....oooooo....',
  ],
  flying: [
    '..............',
    '..............',
    '..............',
    'oo..........oo',
    'obbo.oooo.obbo',
    '.obbobbbbobbo.',
    '..obbbeebbbo..',
    '...obbbbbbo...',
    '..obbbbbbbbo..',
    '.obbo.oo.obbo.',
    'obo........obo',
    '..............',
    '..............',
    '..............',
  ],
  turret: [
    '..............',
    '.....oooo.....',
    '....oaaaao....',
    '...oabbbbao...',
    '..oabbbbbbao..',
    '..oabbeebbao..',
    '..oabbbbbbao..',
    '..oabbbbbbao..',
    '...oabbbbao...',
    '....odddo.....',
    '.....odo......',
    '....odddo.....',
    '..ooddddddoo..',
    '.oooooooooooo.',
  ],
  knight: [
    '.....oooo.....',
    '....ommmmo....',
    '....omeemo....',
    '....ommmmo....',
    '...oommmmoo...',
    '..oabbbbbbao..',
    '.oabbbbbbbbao.',
    '.oabbbbbbbbao.',
    '.oabbbbbbbbao.',
    '..oabbbbbbao..',
    '...obbo.bbo...',
    '...obbo.bbo...',
    '..oddo..oddo..',
    '..oooo..oooo..',
  ],
  spirit: [
    '.....oooo.....',
    '....obbbbo....',
    '...obbbbbbo...',
    '...obeebbbo...',
    '...obbbbbbo...',
    '..oabbbbbbao..',
    '..oabbbbbbao..',
    '..oabbbbbbao..',
    '...obbbbbbo...',
    '...obbbbbbo...',
    '....obbbbo....',
    '....ob.bbo....',
    '.....o..bo....',
    '.........o....',
  ],
  totem: [
    '.....oooo.....',
    '....oaaaao....',
    '....oabbao....',
    '....obeebo....',
    '....obbbbo....',
    '....oaaaao....',
    '....obbbbo....',
    '....obbbbo....',
    '....obeebo....',
    '....obbbbo....',
    '....oddddo....',
    '...oddddddo...',
    '..oddddddddo..',
    '..oooooooooo..',
  ],
  pet_wolf: [
    '..........',
    '.o......o.',
    'obo....obo',
    'obbooooobo',
    '.obbbbbbbo',
    '.obebbbbbo',
    '..obbbbbo.',
    '..obo.obo.',
    '..odo.odo.',
    '..oo...oo.',
  ],
  pet_raven: [
    '..........',
    '..........',
    '...oo.....',
    '..obbo.a..',
    '.obbbbo...',
    'obebbbboo.',
    '.obbbbbbo.',
    '..obbbo...',
    '...o.o....',
    '..........',
  ],
  pet_wisp: [
    '..........',
    '...oooo...',
    '..oaaaao..',
    '.oabbbbao.',
    '.oabebbao.',
    '.oabbbbao.',
    '..oabbao..',
    '...oaao...',
    '....oo....',
    '..........',
  ],
  pet_tortoise: [
    '..........',
    '..oooooo..',
    '.oabbbbao.',
    'oabbabbbao',
    'oabbbbbbao',
    '.oooooooo.',
    'obdbdbdbdo',
    '.oo.oo.oo.',
    '..........',
    '..........',
  ],
  pet_bat: [
    '..........',
    '..........',
    'oo......oo',
    'obbo..obbo',
    '.obbooobo.',
    '..obeebo..',
    '...obbo...',
    '....oo....',
    '..........',
    '..........',
  ],
  pet_cat: [
    '..........',
    '.o.....o..',
    '.oo...oo..',
    '.obbobbo..',
    '.obebebo..',
    '.obbbbbo..',
    '..obbbo.a.',
    '..obbboa..',
    '..odo.od..',
    '..oo..oo..',
  ],
  boss: [
    '......oooooooo......',
    '.....ommmmmmmmo.....',
    '....ommmeemmeemmo...',
    '....ommmmmmmmmmmo...',
    '...oommmmmmmmmmoo...',
    '..oaaddbbbbbbddaao..',
    '.oaaddbbbbbbbbddaao.',
    '.oaddbbbbbbbbbbddao.',
    '.oaddbbbbbbbbbbddao.',
    '.oaddbbbbbbbbbbddao.',
    '..oaabbbbbbbbbbaao..',
    '...oobbbbbbbbbboo...',
    '...obbbbo..obbbbo...',
    '...obbbbo..obbbbo...',
    '...oddddo..oddddo...',
    '...oddddo..oddddo...',
    '..ommmmmo..ommmmmo..',
    '..ooooooo..ooooooo..',
  ],
};

export function mobTexture(shape: string, base: string, accent: string): Texture {
  const rows = SHAPES[shape] ?? SHAPES.humanoid;
  return patternTexture('mob2:' + shape + base + accent, rows, basePalette(base, accent));
}

// ------------------------------------------------------------------ props & decor

const PROPS: Record<string, string[]> = {
  shrine: [
    '......oo......',
    '.....oaao.....',
    '...ooaaaaoo...',
    '..oaaaaaaaao..',
    '..oaabbbbaao..',
    '...oobbbboo...',
    '....obbbbo....',
    '....obbbbo....',
    '...oobbbboo...',
    '..oddddddddo..',
    '.oddddddddddo.',
    '.oooooooooooo.',
  ],
  chest: [
    '..............',
    '..............',
    '..oooooooooo..',
    '.oaaaaaaaaaao.',
    '.oabbbbbbbbao.',
    '.oabbbbbbbbao.',
    '.oooooooooooo.',
    '.obbbboeobbbo.',
    '.obbbboeobbbo.',
    '.obbbbbbbbbbo.',
    '.oddddddddddo.',
    '.oooooooooooo.',
  ],
  altar: [
    '..............',
    '.....rrrr.....',
    '....orrrro....',
    '....obbbbo....',
    '...oobbbboo...',
    '..oabbbbbbao..',
    '..oabbrbbbao..',
    '..oabbbbrbao..',
    '..oabbbbbbao..',
    '.oddddddddddo.',
    '.oddddddddddo.',
    '.oooooooooooo.',
  ],
  item: [
    '....oo....',
    '...oaao...',
    '..oabbao..',
    '.oabbbbao.',
    'oabbwbbbao',
    'oabbbbbbao',
    '.oabbbbao.',
    '..oabbao..',
    '...oaao...',
    '....oo....',
  ],
  consumable: [
    '...oooo...',
    '..oddddo..',
    '...obbo...',
    '..oabbao..',
    '.oabbbbao.',
    '.oabwbbao.',
    '.oabbbbao.',
    '.oabbbbao.',
    '..oaaaao..',
    '...oooo...',
  ],
  heal: [
    '...oooo...',
    '..orrrro..',
    '.orrwrrro.',
    'orrrwrrrro',
    'orwwwwwrro',
    'orrrwrrrro',
    '.orrwrrro.',
    '..orrrro..',
    '...oooo...',
    '..........',
  ],
  // decor
  tombstone: [
    '...oooo...',
    '..obbbbo..',
    '.obbbbbbo.',
    '.obbobbbo.',
    '.obbbbbbo.',
    '.obbbbbbo.',
    '.odbbbbdo.',
    '.odbbbbdo.',
    '.oddddddo.',
    'oddddddddo',
    'oooooooooo',
  ],
  cross: [
    '....oo....',
    '...obbo...',
    '.oobbbboo.',
    'obbbbbbbbo',
    '.oobbbboo.',
    '...obbo...',
    '...obbo...',
    '...obbo...',
    '...obbo...',
    '..oddddo..',
    '.oooooooo.',
  ],
  dead_tree: [
    '......o.......',
    '.....odo...o..',
    '.o...odo..odo.',
    'odo..odo.odo..',
    '.odo.odo.od...',
    '..odoodood....',
    '...oddddo.....',
    '....oddo......',
    '....oddo......',
    '....oddo......',
    '....oddo......',
    '....oddo......',
    '...oddddo.....',
    '..oddddddo....',
    '.oooooooooo...',
  ],
  skull: [
    '..oooo..',
    '.obbbbo.',
    'obbbbbbo',
    'obeebeb.',
    'obbbbbbo',
    '.obobob.',
    '..oooo..',
  ],
  bones: [
    '..o.....o...',
    '.obo...obo..',
    '..oboooobo..',
    '...obbbbo...',
    '..oboooobo..',
    '.obo...obo..',
    '..o.....o...',
  ],
  rock: [
    '....oooo....',
    '..oobbbboo..',
    '.obbbbbbbbo.',
    'obbbbbbbbbbo',
    'obbbbbbbbddo',
    '.oddddddddo.',
    '..oooooooo..',
  ],
  bush: [
    '....oooo....',
    '..oobbbboo..',
    '.obbbbbbbbo.',
    'obbbbobbbbbo',
    'obbbbbbbobbo',
    '.obbbbbbbbo.',
    '..oodddoo...',
    '....odo.....',
  ],
  ember_stone: [
    '...oooo...',
    '..obbbbo..',
    '.obbrbbbo.',
    'obbbbbrbbo',
    'obrbbbbbbo',
    '.oddddddo.',
    '..oooooo..',
  ],
  mushrooms: [
    '..oo....',
    '.oaao.oo',
    'oaaaaoao',
    '.oddo.ao',
    '..od.odo',
    '..od..d.',
  ],
  torch: [
    '..rr..',
    '.rrrr.',
    '.rarr.',
    '..oo..',
    '..hh..',
    '..hh..',
    '..hh..',
    '..hh..',
  ],
};

export function propTexture(kind: string, base: string, accent: string): Texture {
  const rows = PROPS[kind] ?? PROPS.item;
  return patternTexture('prop2:' + kind + base + accent, rows, basePalette(base, accent));
}

export interface DecorKind {
  kind: string;
  color: string;
  accent: string;
  scale: number;
  /** drawn in the unit layer so heroes can walk behind */
  tall: boolean;
}

export const DECOR: Record<number, DecorKind[]> = {
  1: [
    { kind: 'dead_tree', color: '#3a2a2a', accent: '#5a4a3a', scale: 2.4, tall: true },
    { kind: 'bush', color: '#2f3a2a', accent: '#4a5a3a', scale: 2, tall: false },
    { kind: 'tombstone', color: '#5a5566', accent: '#8a8596', scale: 2, tall: true },
    { kind: 'rock', color: '#4a4550', accent: '#6a6570', scale: 2, tall: false },
    { kind: 'mushrooms', color: '#6a3a4a', accent: '#c8ffe0', scale: 2, tall: false },
    { kind: 'cross', color: '#5a4a3a', accent: '#7a6a5a', scale: 2, tall: true },
  ],
  2: [
    { kind: 'tombstone', color: '#55505f', accent: '#8a8596', scale: 2.2, tall: true },
    { kind: 'skull', color: '#d8ccb0', accent: '#ffffff', scale: 2, tall: false },
    { kind: 'bones', color: '#cfc4a8', accent: '#ffffff', scale: 2, tall: false },
    { kind: 'dead_tree', color: '#2e2226', accent: '#4a3a3a', scale: 2.6, tall: true },
    { kind: 'rock', color: '#403a48', accent: '#605a68', scale: 2.2, tall: false },
  ],
  3: [
    { kind: 'skull', color: '#e0d4b8', accent: '#ffffff', scale: 2.2, tall: false },
    { kind: 'bones', color: '#d8ccb0', accent: '#ffffff', scale: 2.2, tall: false },
    { kind: 'ember_stone', color: '#3a2222', accent: '#ff7a2f', scale: 2.4, tall: false },
    { kind: 'dead_tree', color: '#261a1a', accent: '#3a2a2a', scale: 2.8, tall: true },
    { kind: 'cross', color: '#3a3038', accent: '#5a505a', scale: 2.2, tall: true },
  ],
};

// ------------------------------------------------------------------ tiles

export function tileTexture(kind: 'ground' | 'wall', tier: number, variant: number): Texture {
  const key = `tile2:${kind}:${tier}:${variant}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  let seed = (tier * 131 + variant * 17 + (kind === 'wall' ? 7 : 0)) >>> 0;
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  const t = Math.min(2, Math.max(0, tier - 1));
  if (kind === 'ground') {
    const bases = ['#232030', '#261c28', '#2a191f'];
    const base = bases[t];
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);
    // soft mottling
    for (let i = 0; i < 40; i++) {
      const f = 0.85 + rnd() * 0.3;
      ctx.fillStyle = shade(base, f);
      ctx.fillRect(Math.floor(rnd() * size), Math.floor(rnd() * size), 1 + Math.floor(rnd() * 3), 1 + Math.floor(rnd() * 2));
    }
    // cracks
    const cracks = t === 0 ? 1 : 2;
    for (let c = 0; c < cracks; c++) {
      let x = Math.floor(rnd() * size);
      let y = Math.floor(rnd() * size);
      ctx.fillStyle = shade(base, 0.6);
      for (let s = 0; s < 6 + Math.floor(rnd() * 8); s++) {
        ctx.fillRect(x, y, 1, 1);
        x += rnd() < 0.5 ? 1 : 0;
        y += rnd() < 0.7 ? 1 : -1;
        if (x >= size || y >= size || y < 0) break;
      }
    }
    if (t === 0) {
      // grass tufts
      ctx.fillStyle = '#2f4a30';
      for (let i = 0; i < 5; i++) {
        const x = Math.floor(rnd() * size);
        const y = Math.floor(rnd() * size);
        ctx.fillRect(x, y, 1, 2);
        ctx.fillRect(x + 2, y - 1, 1, 3);
      }
    } else if (t === 1) {
      // bone chips
      ctx.fillStyle = '#5a5044';
      for (let i = 0; i < 3; i++) ctx.fillRect(Math.floor(rnd() * size), Math.floor(rnd() * size), 2, 1);
    } else {
      // embers
      for (let i = 0; i < 3; i++) {
        ctx.fillStyle = rnd() < 0.5 ? '#5a2a20' : '#7a3a22';
        ctx.fillRect(Math.floor(rnd() * size), Math.floor(rnd() * size), 1, 1);
      }
    }
  } else {
    const base = ['#4a4454', '#463c48', '#43363e'][t];
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, size, size);
    // bricks
    for (let y = 0; y < size; y += 8) {
      const off = (y / 8) % 2 === 0 ? 0 : 8;
      for (let x = -8; x < size; x += 16) {
        ctx.fillStyle = shade(base, 1.12 + (rnd() - 0.5) * 0.15);
        ctx.fillRect(x + off + 1, y + 1, 14, 6);
        ctx.fillStyle = shade(base, 1.3);
        ctx.fillRect(x + off + 1, y + 1, 14, 1);
        ctx.fillStyle = shade(base, 0.75);
        ctx.fillRect(x + off + 1, y + 6, 14, 1);
      }
    }
    // pits and moss
    for (let i = 0; i < 8; i++) {
      ctx.fillStyle = shade(base, 0.6);
      ctx.fillRect(Math.floor(rnd() * size), Math.floor(rnd() * size), 1, 1);
    }
    if (t === 0) {
      ctx.fillStyle = '#3a5a3a';
      for (let i = 0; i < 4; i++) ctx.fillRect(Math.floor(rnd() * size), Math.floor(rnd() * size), 2, 1);
    }
    ctx.fillStyle = shade(base, 1.45);
    ctx.fillRect(0, 0, size, 2);
    ctx.fillStyle = shade(base, 0.5);
    ctx.fillRect(0, size - 2, size, 2);
  }
  const tex = Texture.from(canvas);
  tex.source.scaleMode = 'nearest';
  cache.set(key, tex);
  return tex;
}

// ------------------------------------------------------------------ projectiles

export interface ProjStyle {
  shape: 'dot' | 'arrow' | 'blade' | 'orb' | 'shard' | 'wave' | 'ring';
  color: string;
  size: number;
  glow?: string;
  trail?: boolean;
}

const PROJ_STYLES: Record<string, ProjStyle> = {
  arrow: { shape: 'arrow', color: '#e8dcc0', size: 12 },
  arrow_light: { shape: 'arrow', color: '#ffe38a', size: 13, glow: '#fff2b3', trail: true },
  arrow_storm: { shape: 'arrow', color: '#b9ecff', size: 13, glow: '#e8fbff', trail: true },
  arrow_bone: { shape: 'arrow', color: '#d9d2bf', size: 11 },
  lance_light: { shape: 'blade', color: '#fff2b3', size: 30, glow: '#ffe38a', trail: true },
  fireball: { shape: 'orb', color: '#ff7a2f', size: 12, glow: '#ffb347', trail: true },
  comet: { shape: 'orb', color: '#ff4d1a', size: 20, glow: '#ffb347', trail: true },
  ember: { shape: 'dot', color: '#ffb347', size: 7, glow: '#ff7a2f', trail: true },
  bolt_void: { shape: 'orb', color: '#b48cff', size: 9, glow: '#6a3cff', trail: true },
  bolt_void_big: { shape: 'orb', color: '#d8c8ff', size: 13, glow: '#7a4cff', trail: true },
  bolt_dark: { shape: 'orb', color: '#7a2a4a', size: 9, glow: '#ff7ab8', trail: true },
  bolt_storm: { shape: 'shard', color: '#c9f3ff', size: 10, glow: '#7cc7ff', trail: true },
  bolt_totem: { shape: 'dot', color: '#ffe9b0', size: 7 },
  shard_frost: { shape: 'shard', color: '#bfefff', size: 9, glow: '#7fdcff', trail: true },
  shard_bone: { shape: 'shard', color: '#efe6d2', size: 8 },
  shard_mirror: { shape: 'shard', color: '#dfe9ff', size: 8, glow: '#ffffff' },
  blade: { shape: 'blade', color: '#cfd8e6', size: 16 },
  blade_small: { shape: 'blade', color: '#e6eef8', size: 10 },
  blade_big: { shape: 'blade', color: '#9aa7b8', size: 28 },
  spore: { shape: 'dot', color: '#9ee37d', size: 9, glow: '#c8ff8a' },
  wave_slash: { shape: 'wave', color: '#ffcf7a', size: 16, glow: '#ff9350' },
  wave_water: { shape: 'wave', color: '#a8e0e8', size: 18, glow: '#bff0ff' },
  anchor: { shape: 'ring', color: '#8aa8b8', size: 24 },
  note: { shape: 'orb', color: '#ffd9a0', size: 8, glow: '#fff2c0', trail: true },
  spark: { shape: 'dot', color: '#c8ff8a', size: 7, glow: '#a7ff5c', trail: true },
};

export function projStyle(visual: string): ProjStyle {
  return PROJ_STYLES[visual] ?? { shape: 'dot', color: '#ffffff', size: 8 };
}

export function projTexture(visual: string): Texture {
  const key = 'proj:' + visual;
  const hit = cache.get(key);
  if (hit) return hit;
  const st = projStyle(visual);
  const s = st.size * 2 + 8;
  const canvas = document.createElement('canvas');
  canvas.width = s;
  canvas.height = s;
  const ctx = canvas.getContext('2d')!;
  const c = s / 2;
  if (st.glow) {
    ctx.shadowColor = st.glow;
    ctx.shadowBlur = 7;
  }
  ctx.fillStyle = st.color;
  ctx.strokeStyle = st.color;
  switch (st.shape) {
    case 'dot':
    case 'orb':
      ctx.beginPath();
      ctx.arc(c, c, st.size / 2, 0, Math.PI * 2);
      ctx.fill();
      if (st.shape === 'orb') {
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(c - st.size * 0.15, c - st.size * 0.15, st.size * 0.18, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    case 'arrow':
      ctx.beginPath();
      ctx.moveTo(c + st.size, c);
      ctx.lineTo(c - st.size * 0.6, c - st.size * 0.28);
      ctx.lineTo(c - st.size * 0.3, c);
      ctx.lineTo(c - st.size * 0.6, c + st.size * 0.28);
      ctx.closePath();
      ctx.fill();
      break;
    case 'blade':
      ctx.beginPath();
      ctx.moveTo(c + st.size, c);
      ctx.lineTo(c, c - st.size * 0.22);
      ctx.lineTo(c - st.size, c);
      ctx.lineTo(c, c + st.size * 0.22);
      ctx.closePath();
      ctx.fill();
      break;
    case 'shard':
      ctx.beginPath();
      ctx.moveTo(c + st.size * 0.8, c);
      ctx.lineTo(c - st.size * 0.4, c - st.size * 0.4);
      ctx.lineTo(c - st.size * 0.8, c);
      ctx.lineTo(c - st.size * 0.4, c + st.size * 0.4);
      ctx.closePath();
      ctx.fill();
      break;
    case 'wave':
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(c - st.size * 0.4, c, st.size, -0.9, 0.9);
      ctx.stroke();
      break;
    case 'ring':
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(c, c, st.size / 2, 0, Math.PI * 2);
      ctx.stroke();
      break;
  }
  const tex = Texture.from(canvas);
  cache.set(key, tex);
  return tex;
}

export function circleTexture(radius: number, color: string, glow?: string): Texture {
  const key = `circle:${radius}:${color}:${glow ?? ''}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const s = radius * 2 + 8;
  const canvas = document.createElement('canvas');
  canvas.width = s;
  canvas.height = s;
  const ctx = canvas.getContext('2d')!;
  if (glow) {
    ctx.shadowColor = glow;
    ctx.shadowBlur = 4;
  }
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(s / 2, s / 2, radius, 0, Math.PI * 2);
  ctx.fill();
  const tex = Texture.from(canvas);
  cache.set(key, tex);
  return tex;
}

/** Soft radial light, white; tint the sprite for colour and use additive blending. */
export function lightTexture(size = 256): Texture {
  const key = 'light:' + size;
  const hit = cache.get(key);
  if (hit) return hit;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  g.addColorStop(0.7, 'rgba(255,255,255,0.15)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = Texture.from(canvas);
  cache.set(key, tex);
  return tex;
}

/** Irregular blood / ichor splat used as a ground decal. */
export function decalTexture(variant: number, color: string): Texture {
  const key = `decal:${variant}:${color}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const size = 48;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  let seed = (variant * 7919 + 13) >>> 0;
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  ctx.fillStyle = color;
  for (let i = 0; i < 9; i++) {
    const r = 3 + rnd() * 7;
    const a = rnd() * Math.PI * 2;
    const d = rnd() * 12;
    ctx.beginPath();
    ctx.arc(size / 2 + Math.cos(a) * d, size / 2 + Math.sin(a) * d, r, 0, Math.PI * 2);
    ctx.fill();
  }
  for (let i = 0; i < 10; i++) {
    ctx.fillRect(Math.floor(rnd() * size), Math.floor(rnd() * size), 1 + Math.floor(rnd() * 2), 1 + Math.floor(rnd() * 2));
  }
  const tex = Texture.from(canvas);
  cache.set(key, tex);
  return tex;
}

export const RARITY_COLORS = ['#b8b8b8', '#4f9cff', '#c65cff', '#ffb347'];
